import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  ClipboardList,
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  User,
  Calendar,
  Clock,
  Building2,
  DoorOpen,
  CalendarCheck,
  Users,
  Settings,
  FileText,
  Shield,
  Bell,
  CreditCard,
  UserPlus,
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type AuditLog } from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'
import { PageTabs } from '../components/PageTabs'

// Map entity types to icons
const entityIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  organisation: Building2,
  building: Building2,
  room: DoorOpen,
  booking: CalendarCheck,
  user: User,
  membership: Users,
  settings: Settings,
  terms: FileText,
  pli_certificate: Shield,
  activity_type: FileText,
  access_request: UserPlus,
  session: User,
  notification: Bell,
  payment: CreditCard,
  room_schedule: Clock,
  invite: UserPlus,
}

// Map actions to colors
const actionColors: Record<string, string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  approve: 'bg-emerald-100 text-emerald-800',
  reject: 'bg-orange-100 text-orange-800',
  cancel: 'bg-gray-100 text-gray-800',
  login: 'bg-primary-100 text-primary-800',
  logout: 'bg-slate-100 text-slate-800',
  login_failed: 'bg-red-100 text-red-800',
  suspend: 'bg-yellow-100 text-yellow-800',
  restore: 'bg-teal-100 text-teal-800',
  invite: 'bg-teal-100 text-teal-800',
  accept: 'bg-green-100 text-green-800',
}

// Entity type options for filter
const entityTypeOptions = [
  { value: '', label: 'All Entity Types' },
  { value: 'booking', label: 'Bookings' },
  { value: 'room', label: 'Rooms' },
  { value: 'building', label: 'Buildings' },
  { value: 'user', label: 'Users' },
  { value: 'membership', label: 'Memberships' },
  { value: 'session', label: 'Sessions' },
  { value: 'terms', label: 'Terms' },
  { value: 'pli_certificate', label: 'PLI Certificates' },
  { value: 'activity_type', label: 'Activity Types' },
  { value: 'invite', label: 'Invites' },
  { value: 'settings', label: 'Settings' },
]

// Action options for filter
const actionOptions = [
  { value: '', label: 'All Actions' },
  { value: 'create', label: 'Created' },
  { value: 'update', label: 'Updated' },
  { value: 'delete', label: 'Deleted' },
  { value: 'approve', label: 'Approved' },
  { value: 'reject', label: 'Rejected' },
  { value: 'cancel', label: 'Cancelled' },
  { value: 'login', label: 'Login' },
  { value: 'logout', label: 'Logout' },
  { value: 'login_failed', label: 'Failed Login' },
  { value: 'suspend', label: 'Suspended' },
  { value: 'restore', label: 'Restored' },
  { value: 'invite', label: 'Invited' },
]

export function AuditPage() {
  const navigate = useNavigate()
  const { currentOrg, isAuthenticated, isLoading, checkAuth } = useAuthStore()
  const [logs, setLogs] = useState<AuditLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Pagination
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  
  // Filters
  const [search, setSearch] = useState('')
  const [entityType, setEntityType] = useState('')
  const [action, setAction] = useState('')
  const [userId, setUserId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  
  // Users for filter dropdown
  const [auditUsers, setAuditUsers] = useState<{ userId: string | null; userName: string; userEmail: string | null }[]>([])
  
  // Stats
  const [stats, setStats] = useState<{
    total: number
    last24Hours: number
    byAction: Record<string, number>
    byEntityType: Record<string, number>
  } | null>(null)
  
  // Expanded rows
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set())

  useEffect(() => {
    checkAuth()
  }, [])
  
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: '/login', search: { returnTo: undefined } })
    }
  }, [isLoading, isAuthenticated, navigate])
  
  useEffect(() => {
    if (!isLoading && isAuthenticated && currentOrg) {
      // Check if user is admin
      if (currentOrg.role !== 'admin') {
        navigate({ to: '/dashboard' })
        return
      }
      loadData()
      loadStats()
      loadUsers()
    }
  }, [isLoading, isAuthenticated, currentOrg, page, entityType, action, userId, startDate, endDate])
  
  const loadData = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await api.audit.list({
        page,
        pageSize: 50,
        entityType: entityType || undefined,
        action: action || undefined,
        userId: userId || undefined,
        search: search || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      })
      setLogs(result.logs)
      setTotalPages(result.pagination.totalPages)
      setTotal(result.pagination.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs')
    } finally {
      setLoading(false)
    }
  }
  
  const loadStats = async () => {
    try {
      const result = await api.audit.getStats()
      setStats(result)
    } catch (err) {
      console.error('Failed to load stats:', err)
    }
  }
  
  const loadUsers = async () => {
    try {
      const result = await api.audit.getUsers()
      setAuditUsers(result.users)
    } catch (err) {
      console.error('Failed to load users:', err)
    }
  }
  
  const handleSearch = () => {
    setPage(1)
    loadData()
  }
  
  const handleExport = () => {
    const url = api.audit.getExportUrl({
      entityType: entityType || undefined,
      action: action || undefined,
      userId: userId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      format: 'csv',
    })
    window.open(url, '_blank')
  }
  
  const toggleLogExpanded = (logId: string) => {
    const newExpanded = new Set(expandedLogs)
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId)
    } else {
      newExpanded.add(logId)
    }
    setExpandedLogs(newExpanded)
  }
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  
  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }
  
  return (
    <DashboardLayout title="Settings" subtitle="Track all changes and activities for compliance">
      <PageTabs
        tabs={[
          { label: 'Settings', to: '/dashboard/settings' },
          { label: 'Audit', to: '/dashboard/audit' },
        ]}
        active="/dashboard/audit"
      />
      {/* Header actions */}
      <div className="mb-6 flex items-center justify-end gap-2">
        <button
          onClick={loadData}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          onClick={handleExport}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Total Records</p>
            <p className="text-2xl font-semibold text-gray-900">{stats.total.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Last 24 Hours</p>
            <p className="text-2xl font-semibold text-primary-600">{stats.last24Hours.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Most Common Action</p>
            <p className="text-lg font-semibold text-gray-900">
              {Object.entries(stats.byAction).sort(([,a], [,b]) => b - a)[0]?.[0] || '-'}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <p className="text-sm text-gray-500">Most Active Entity</p>
            <p className="text-lg font-semibold text-gray-900">
              {Object.entries(stats.byEntityType).sort(([,a], [,b]) => b - a)[0]?.[0] || '-'}
            </p>
          </div>
        </div>
      )}
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by user name, email, or entity ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Search
            </button>
          </div>
          
          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
          </button>
        </div>
        
        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
              <select
                value={entityType}
                onChange={(e) => { setEntityType(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              >
                {entityTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Action</label>
              <select
                value={action}
                onChange={(e) => { setAction(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              >
                {actionOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
              <select
                value={userId}
                onChange={(e) => { setUserId(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              >
                <option value="">All Users</option>
                {auditUsers.map(u => (
                  <option key={u.userId || 'system'} value={u.userId || ''}>
                    {u.userName} {u.userEmail ? `(${u.userEmail})` : ''}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1) }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
          {error}
        </div>
      )}
      
      {/* Audit Log Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-2" />
            <p className="text-gray-500">Loading audit logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">No audit logs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Changed By
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Summary
                  </th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log) => {
                  const IconComponent = entityIcons[log.entityType] || ClipboardList
                  const isExpanded = expandedLogs.has(log.id)
                  
                  return (
                    <>
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(log.changedAt)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionColors[log.action.toLowerCase()] || 'bg-gray-100 text-gray-800'}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <IconComponent className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">{log.entityType}</span>
                          </div>
                          <div className="text-xs text-gray-500 truncate max-w-32" title={log.entityId}>
                            {log.entityId}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">{log.changedBy}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-md truncate" title={log.summary}>
                          {log.summary}
                        </td>
                        <td className="px-4 py-3">
                          {log.details.length > 0 && (
                            <button
                              onClick={() => toggleLogExpanded(log.id)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          )}
                        </td>
                      </tr>
                      
                      {/* Expanded Details */}
                      {isExpanded && log.details.length > 0 && (
                        <tr key={`${log.id}-details`}>
                          <td colSpan={6} className="px-4 py-3 bg-gray-50">
                            <div className="text-sm">
                              <h4 className="font-medium text-gray-700 mb-2">Change Details</h4>
                              <div className="grid gap-2">
                                {log.details.map((detail, idx) => (
                                  <div key={idx} className="flex items-start gap-4 py-1 border-b border-gray-100 last:border-0">
                                    <span className="font-medium text-gray-600 min-w-32">{detail.field}</span>
                                    <span className="text-gray-400">from</span>
                                    <span className="text-red-600 line-through">{formatValue(detail.from)}</span>
                                    <span className="text-gray-400">to</span>
                                    <span className="text-green-600">{formatValue(detail.to)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing page {page} of {totalPages} ({total.toLocaleString()} total records)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
